import EditPlace from "@/utils/presenters/EditPlace.ts";
import OwnerType from "@/utils/constants/OwnerType.ts";
import PreferencesRequest from "@/utils/constants/PreferencesRequest.ts";
import MetaData from "@/utils/common/MetaData.ts";
import SecurityUtils from "@/utils/login/SecurityUtils";
import DTOSecurityFieldAuthority from "@/utils/constants/DTOSecurityFieldAuthority";
import ViewMode from "@/utils/constants/ViewMode";
import Translator, {ElementTranslationInfo} from "@/utils/common/Translator";
import ClientCache from "@/utils/login/ClientCache";
import GenerationType from "@/utils/constants/GenerationType.ts";
import TransactionRequestStatus from "@/utils/constants/TransactionRequestStatus.ts";
import DocumentFileStatus from "@/utils/constants/DocumentFileStatus.ts";
import ApprovalCaseState from "@/utils/constants/ApprovalCaseState.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import CommonConstants from "@/utils/constants/CommonConstants.ts";
import DomainBaseEntities from "@/utils/constants/DomainBaseEntities.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import BSCEntities from "@/utils/constants/BSCEntities.ts";
import DomainBaseFieldIDs from "@/utils/constants/DomainBaseFieldIDs.ts";
import GridSize from "@/utils/constants/GridSize.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import EditScreenFieldLayout from "@/utils/constants/EditScreenFieldLayout.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import ApprovalInitiators from "@/utils/constants/ApprovalInitiators.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import {useToastManager} from "@/stores/ToastsComposable.ts";
import SimpleEntities from "@/utils/constants/SimpleEntities.ts";
import {Block} from "@/utils/common/BlockUtils.ts";
import CriteriaField = namaTypes.CriteriaField;
import NaMaMetaData = namaTypes.NaMaMetaData;
import QuestionField = namaTypes.QuestionField;
import EditScreenList = namaTypes.EditScreenList;
import FetchPreferencesRequest = namaTypes.FetchPreferencesRequest;
import UIPreferences = namaTypes.UIPreferences;
import TitledID = namaTypes.TitledID;
import CompositeEditScreenField = namaTypes.CompositeEditScreenField;
import DTOTimelineData = namaTypes.DTOTimelineData;
import EditScreenPage = namaTypes.EditScreenPage;
import EditScreenLayout = namaTypes.EditScreenLayout;
import DTOApprovalCaseStep = namaTypes.DTOApprovalCaseStep;

export default class EditViewUtils {

  constructor(private readonly presenter?: EditPresenter) {
    this.presenter = presenter;
  }

  static async fetchLayoutPreferences(place: EditPlace): Promise<namaTypes.UIPreferences> {
    let req: Record<any, any> = {
      owner: {
        ownerType: OwnerType.Entity,
        ownerId: place.entityType
      },
      properties: [PreferencesRequest.EDIT_LAYOUT, PreferencesRequest.META_DATA, PreferencesRequest.LIST_LAYOUT_PREFERENCES],
      editLayoutScreenName: place.calcEditViewName(),
      listLayoutScreenName: place.listViewName
    };
    return await MetaData.fetchPreferences(req, (preferences: namaTypes.UIPreferences) => preferences);
  }

  static isPageHidden(page: namaTypes.EditScreenPage, entityType: string, pageIndex: number) {
    return page == null
      || !SecurityUtils.userCanAnyFeature(page?.relatedToFeature!)
      || ObjectChecker.areEqual(SecurityUtils.getPageAuthority(entityType, page.title?.resourceId, pageIndex),
        DTOSecurityFieldAuthority.InVisible);
  }

  static getEditViewTitle(place: EditPlace, object: namaTypes.BaseEntityDTO): string {
    let detailsViewTitlePrefix: string = Translator.getDetailsViewTitlePrefix(object.entityType, ClientCache.getMenuLineBySrc(place.src));
    let code: string | undefined = Translator.getCode(object);
    let name: string = Translator.getNameConsideringDescriptors(object);
    if (name && ObjectChecker.areNotEqual(code, name))
      code = code + " - " + name;
    let isCodeEmpty: boolean = ObjectChecker.isEmptyOrNull(code);
    return detailsViewTitlePrefix + (isCodeEmpty ? "" : ": " + code);
  }

  static getCurrentStates(object: Record<string, any>, place?: EditPlace): EditViewState[] {
    const states: EditViewState[] = [];
    const generationType: string | undefined = object.generationType;
    if (generationType && ObjectChecker.areNotEqual(generationType, GenerationType.NotGenerated)
      && ObjectChecker.areNotEqual(generationType, GenerationType.GeneratedEditable)) {
      states.push({
        label: Translator.translateId('GenerationType.' + generationType),
        status: generationType
      });
    }
    let addedRequestsStatus: boolean = false;
    let requests = object.requests;
    if (requests) {
      let requestsStatuses = requests.statuses;
      if (requestsStatuses) {
        for (let requestStatus of requestsStatuses) {
          let currentLineStatus: string | undefined = requestStatus.status;
          if (currentLineStatus && ObjectChecker.areNotEqual(currentLineStatus, TransactionRequestStatus.Processed)) {
            states.push({
              label: Translator.translateId('TransactionRequestStatus.' + currentLineStatus),
              status: currentLineStatus
            });
            addedRequestsStatus = true;
            break;
          }
        }
      }
    }
    if (!addedRequestsStatus) {
      let documentFileStatus = object.documentFileStatus;
      if (documentFileStatus) {
        if (place && EditViewUtils.isNew(place))
          states.push({
            label: Translator.translateId("New"),
            status: "new"
          })
        else
          states.push({
            label: Translator.translateId('DocumentFileStatus.' + documentFileStatus) + this.calcApprovalInitiatorSubTitle(object) + this.addDocumentVersionIfNeeded(place),
            status: documentFileStatus
          });
        if (ObjectChecker.areEqual(documentFileStatus, DocumentFileStatus.Stable) && object.draftCreated) {
          states.push({
            label: Translator.translateId('DocumentFileStatus.' + DocumentFileStatus.Draft),
            status: DocumentFileStatus.Draft
          });
        }
      } else if (this.isApprovalPending(object)) {
        states.push({
          label: Translator.translateId('ApprovalCaseState.' + ApprovalCaseState.InProgress) + this.calcApprovalInitiatorSubTitle(object),
          status: ApprovalCaseState.InProgress
        });
      } else if (object.draftCreated) {
        states.push({
          label: Translator.translateId('DocumentFileStatus.' + DocumentFileStatus.Draft),
          status: DocumentFileStatus.Draft
        });
      }
      if (object.revised) {
        states.push({
          label: Translator.translateId("revised"),
          status: "Revised"
        });
        let reviseLevel: string = this.fetchDisplayLevel(object); //icon => counter
        if (reviseLevel)
          states.push({
            label: Translator.translateId(reviseLevel),
            status: "RevisedLevel"
          });
      }
    }
    if (object.preventUsage) {
      states.push({
        label: Translator.translateId("usagePrevented"),
        status: "usagePrevented"
      });
    }
    if (!states.length) {
      if (object.commitedBefore) {
        states.push({
          label: Translator.translateId('DocumentFileStatus.' + DocumentFileStatus.Stable),
          status: DocumentFileStatus.Stable
        });
      }
    }
    if (!states.length)
      states.push({
        label: Translator.translateId("New"),
        status: "new"
      })
    return states;
  }

  private static calcApprovalInitiatorSubTitle(object: Record<string, any>) {
    let initiator = object.currentApprovalCase?.initiator;
    if (!initiator || initiator === ApprovalInitiators.Commit)
      return "";
    return " - " + Translator.translateId("ApprovalInitiators." + initiator);
  }

  static isApprovalPending(object: namaTypes.BaseEntityDTO): boolean {
    return ObjectChecker.areEqual(object.documentFileStatus, DocumentFileStatus.ApprovalPending)
      || ObjectChecker.areEqual(object.currentApprovalCase?.state, ApprovalCaseState.InProgress);
  }

  static fetchDisplayLevel(object: namaTypes.BaseEntityDTO): string {
    let reviseLevel: number = this.currentReviseLevelInt(object);
    let toBeRevisedLevels: number | undefined = ClientCache.findEntityMaxReviseCount(object.entityType!).reviseLevel;
    if (reviseLevel == toBeRevisedLevels && toBeRevisedLevels == 1)
      return "";
    return this.toReviseLevel(reviseLevel);
  }

  static toReviseLevel(reviseLevel: number): string {
    return 'L' + reviseLevel;
  }

  static currentReviseLevelInt(object: namaTypes.BaseEntityDTO | undefined): number {
    if (!object)
      return 0;
    if (!object.reviseLevel && object.revised)
      return 1;
    let levelString: string | undefined = object.reviseLevel;
    if (!levelString)
      return 0;
    return parseInt(levelString.replace("L", ""));
  }

  static isNew(place: EditPlace): boolean {
    return ObjectChecker.areEqual(place.view, ViewMode.NEW);
  }

  static getFieldMetaData(metaData: namaTypes.NaMaMetaData | undefined, fieldId: string | null | undefined): namaTypes.FieldMetaData | undefined {
    if (!fieldId || !metaData)
      return undefined;
    return metaData.fieldsMetaData?.find(f => f.fieldId === fieldId);
  }

  static createGenericRefEntityTypeMeta(metadata: namaTypes.FieldMetaData): namaTypes.FieldMetaData {
    let typeId: string = (metadata.fieldId ? metadata.fieldId + "." : "") + CommonFieldIds.ENTITY_TYPE;
    let typeMetaData: namaTypes.FieldMetaData = {...metadata};
    typeMetaData.fieldId = typeId;
    typeMetaData.fieldType = FieldType.EntityType;
    typeMetaData.allowedValues = ClientCache.calcAllowedValuesForEntityType(metadata);
    return typeMetaData;
  }

  static getCodeSateLiteFieldIds(entityType: string | undefined) {
    if (!entityType)
      return [];
    if (entityType === DomainBaseEntities.DocumentBook || entityType === DomainBaseEntities.DocumentTerm)
      return ["documentType"];
    if (entityType === DomainBaseEntities.MasterGroup)
      return ["forType"];
    return [];
  }

  static isCriteriaDefinitionCriteria(owner: namaTypes.Owner | undefined, fieldId?: string | undefined) {
    return owner?.ownerId === BSCEntities.CriteriaDefinition && fieldId?.startsWith(DomainBaseFieldIDs.CriteriaDefinition_criteria);
  }

  static fetchDiscussionList(): EditScreenList {
    let discussionList: EditScreenList = {
      title: {
        "resourceId": 'discussionsListViewId',
        "empty": false
      },
      fieldId: "discussionsListViewId",
      showInQuickCreate: false,
      linkType: "QuestionField",
      linkFieldId: "id",
      listViewEntityType: DomainBaseEntities.DocumentBook,
      listViewId: "discussionsListViewId",
      collapsible: true,
      openByDefault: false,
      id: "discussionsListViewId",
      size: GridSize.Large,
      "editScreenList": true,
      "discussionBlock": false,
      "editScreenBlock": true
    }
    discussionList['namaClassTypeId'] = 'EditScreenList';
    return discussionList;
  }

  static fetchDiscussionEditor(): Record<string, any> {
    let discussionEditor: Record<string, any> = {
      title: {
        resourceId: DomainBaseFieldIDs.DiscussionRecord_discussion,
        empty: false
      },
      showInQuickCreate: false
    }
    discussionEditor['namaClassTypeId'] = 'DiscussionEditorWidget';
    return discussionEditor;
  }

  static fetchCriteriaBlock(): Record<string, any> {
    // TODO :: return Criteria block
    let criteriaBlock: Record<string, any> = {
      title: {
        resourceId: 'criteria',
        empty: false
      },
      showInQuickCreate: false,
      editScreenGroup: true,
      discussionBlock: true,
      editScreenBlock: true
    }
    criteriaBlock['namaClassTypeId'] = 'CriteriaEditorWidget';
    return criteriaBlock;
  }

  static createDiscussionFields(entityType: string, prefix: string, layout?: EditScreenLayout): QuestionField[] {
    let questionFields: QuestionField[] = [];
    if (!layout?.removeDiscussionField)
      questionFields.push(NamaCommon.createQuestionField(prefix + DomainBaseFieldIDs.DiscussionRecord_discussion, FieldType.BigText, layout?.discussionFieldLayout ?? EditScreenFieldLayout.spanned2 , ClientCache.isRichTextField(entityType, DomainBaseFieldIDs.DiscussionRecord_discussion)));
    if (!layout?.removeDiscussionAtt1)
      questionFields.push(NamaCommon.createQuestionField(prefix + DomainBaseFieldIDs.DiscussionRecord_attachment1, FieldType.Binary, EditScreenFieldLayout.normal));
    if (!layout?.removeDiscussionAtt2)
      questionFields.push(NamaCommon.createQuestionField(prefix + DomainBaseFieldIDs.DiscussionRecord_attachment2, FieldType.Binary, EditScreenFieldLayout.normal));
    if (!layout?.removeDiscussionAtt3)
      questionFields.push(NamaCommon.createQuestionField(prefix + DomainBaseFieldIDs.DiscussionRecord_attachment3, FieldType.Binary, EditScreenFieldLayout.normal));
    if (!layout?.removeDiscussionAtt4)
      questionFields.push(NamaCommon.createQuestionField(prefix + DomainBaseFieldIDs.DiscussionRecord_attachment4, FieldType.Binary, EditScreenFieldLayout.normal));
    if (!layout?.removeDiscussionRef1)
      questionFields.push(NamaCommon.createQuestionFieldWithAllowedValues(prefix + DomainBaseFieldIDs.DiscussionRecord_ref1, FieldType.Genericreference, ClientCache.getAllowedValuesFor(entityType, "discussion.ref1"), EditScreenFieldLayout.normal));
    if (!layout?.removeDiscussionRef2)
      questionFields.push(NamaCommon.createQuestionFieldWithAllowedValues(prefix + DomainBaseFieldIDs.DiscussionRecord_ref2, FieldType.Genericreference, ClientCache.getAllowedValuesFor(entityType, "discussion.ref2"), EditScreenFieldLayout.normal));
    if (!layout?.removeDiscussionDate1)
      questionFields.push(NamaCommon.createQuestionField(prefix + DomainBaseFieldIDs.DiscussionRecord_date1, FieldType.Date, EditScreenFieldLayout.normal));
    if (!layout?.removeDiscussionDate2)
      questionFields.push(NamaCommon.createQuestionField(prefix + DomainBaseFieldIDs.DiscussionRecord_date2, FieldType.Date, EditScreenFieldLayout.normal));
    if (!layout?.removeDiscussionTime1)
      questionFields.push(NamaCommon.createQuestionField(prefix + DomainBaseFieldIDs.DiscussionRecord_time1, FieldType.Time, EditScreenFieldLayout.normal));
    if (!layout?.removeDiscussionTime2)
      questionFields.push(NamaCommon.createQuestionField(prefix + DomainBaseFieldIDs.DiscussionRecord_time2, FieldType.Time, EditScreenFieldLayout.normal));
    return questionFields
  }

  static createCriteriaFieldsFromMetadata(metadata: NaMaMetaData | undefined): CriteriaField[] {
    const ownerId: string | undefined = metadata?.owner?.ownerId;
    let criteriaFields: CriteriaField[] = [];
    if (!metadata || !metadata.fieldsMetaData)
      return []
    for (let fieldMetadata of metadata.fieldsMetaData) {
      if (CommonConstants.IGNORED_FIELDS.contains(fieldMetadata.fieldId ?? ''))
        continue;
      let id: string = ownerId + "." + (fieldMetadata.fieldId ?? '');
      if (Translator.translateId(id) == id)
        continue;
      criteriaFields.push({id: fieldMetadata.fieldId, metaData: fieldMetadata});
    }
    return criteriaFields;
  }

  static getOwnerTypeField(ownerId: string | undefined): string {
    if (ownerId && ownerId === BSCEntities.CriteriaDefinition)
      return DomainBaseFieldIDs.CriteriaDefinition_forType;
    return '';
  }

  static async fetchCriteriaMetadata(ownerId: string | undefined): Promise<NaMaMetaData | undefined> {
    let request: FetchPreferencesRequest = {
      owner: {
        ownerType: OwnerType.Entity,
        ownerId: ownerId
      },
      properties: [PreferencesRequest.META_DATA]
    };
    return await MetaData.fetchPreferences(request, async (result: UIPreferences | undefined) => {
      if (!result)
        return undefined;
      return result.metaData;
    });
  }

  searchForTitledIdAndPushToTranslations(listOfElementArr: TitledID[][], translations: ElementTranslationInfo[]) {
    if (!listOfElementArr)
      return;
    for (let elements of listOfElementArr) {
      if (!elements)
        continue;
      for (let el of elements) {
        this.pushToTranslations(el, translations)
      }
    }
  }

  getTranslations(): ElementTranslationInfo[] {
    const translations: ElementTranslationInfo[] = [];
    const pages = this.presenter?.pages.value
    if (!pages)
      return translations;
    for (let page of pages) {
      this.pushToTranslations(page, translations)
      if (!page.blocks)
        continue;
      for (let b of page.blocks) {
        let block = b as any;
        this.pushToTranslations(block, translations);
        const elements = [block.fields, block.actions, block.columns]
        this.searchForTitledIdAndPushToTranslations(elements, translations);
      }
    }

    for (let moreAction of (this.presenter?.layout?.moreActions || [])) {
      this.pushToTranslations(moreAction, translations)
    }
    return translations
  }

  private pushToTranslations(element: TitledID, translations: ElementTranslationInfo[]) {
    if (!this.presenter)
      return;
    if (!element)
      return;
    const title = element.title;
    const id = title?.resourceId || element.id
    const info = {
      forType: this.presenter.entityType,
      id,
      arabicText: title?.arabicText,
      englishText: title?.englishText,
      finalId: Translator.calcTranslatedFinalId(id, this.presenter.entityType),
      currentArabic: Translator.isArabic() ? Translator.translateTitledId(this.presenter.entityType, element) : Translator.translateTitledIdInOtherLang(this.presenter.entityType, element),
      currentEnglish: Translator.isEnglish() ? Translator.translateTitledId(this.presenter.entityType, element) : Translator.translateTitledIdInOtherLang(this.presenter.entityType, element),
    }
    if (id)
      translations.push(info)
    let compositeField = element as CompositeEditScreenField;
    if (compositeField.subFields) {
      for (let field of compositeField.subFields)
        this.pushToTranslations(field, translations);
    }
  }

  static clearErrors() {
    useToastManager().closeErrorToast();
    useToastManager().closeMessageToast();
    useToastManager().closeTooltipToast();
    useErrorsStore().clearErrors();
  }

  static approvalCaseToTimelineData(presenter: EditPresenter): DTOTimelineData[] {
    const record = presenter.fetchRecord()
    const currentApprovalCases: DTOApprovalCaseStep[] = NamaCommon.getFieldValue(record, "steps")
    const timelineData: DTOTimelineData[] = []
    for (let approvalCase of currentApprovalCases) {
      const item: DTOTimelineData = {};
      item.date = approvalCase.approvalDate;
      item.subtitleAr = approvalCase.approvalStepSeq
      item.subtitleEn = approvalCase.approvalStepSeq
      if (ObjectChecker.isNotEmptyOrNull(approvalCase.approvalStepName1))
        item.subtitleAr += ": " + approvalCase.approvalStepName1;
      if (ObjectChecker.isNotEmptyOrNull(approvalCase.approvalStepName2))
        item.subtitleEn += ":" + approvalCase.approvalStepName2;
      item.headerAr = approvalCase.actualResponsible;
      item.actionType = approvalCase.decision;
      timelineData.push(item);
    }
    return timelineData;
  }


  static addTimelinePageForApprovalCase(presenter: EditPresenter) {
    if (ObjectChecker.areNotEqual(presenter.entityType, SimpleEntities.ApprovalCase))
      return;
    const timelinePage: EditScreenPage = {
      title: Translator.toNaMaText("Timeline"),
      blocks: [this.timelineBlock(), this.nextApprovalBlock()]
    }
    presenter.prependPage(timelinePage);
  }

  static timelineBlock(): Block {
    return {
      title: Translator.toNaMaText("steps"),
      fieldId: "steps",
      editScreenBlock: true,
      namaClassTypeId: "EditScreenTimeline",
      iconCode: "nm-timeline",
      size: GridSize.Medium
    }
  }

  static nextApprovalBlock(): Block {
    return {
      title: Translator.toNaMaText("nextCandidates"),
      fieldId: "nextCandidates",
      editScreenBlock: true,
      namaClassTypeId: "EditScreenBadges",
      iconCode: "nm-person",
      size: GridSize.Tiny
    }
  }

  private static addDocumentVersionIfNeeded(place: EditPlace | undefined) {
    if (place && place.version)
      return ' - ' + Translator.translateId('versionNo') + ' ' + place?.version;
    return '';
  }
}

export interface EditViewState {
  label: string,
  status?: string
}
